import { DATA } from '@/data'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { Github, Linkedin, Mail } from 'lucide-react'
import { ProjectCard } from '@/components/project-card'
import Glitch from '@/components/Glitch'
import SEO from '@/components/SEO'
import StructuredData from '@/components/StructuredData'

/* ─── Typing-once hook ─────────────────────────────────────── */
function useTypingSequence(
    lines: string[],
    charDelay = 40,
    lineDelay = 500,
) {
    const [revealed, setRevealed] = useState<string[]>([])
    const [currentLine, setCurrentLine] = useState(0)
    const [currentChar, setCurrentChar] = useState(0)
    const [done, setDone] = useState(false)

    useEffect(() => {
        if (currentLine >= lines.length) { setDone(true); return }
        if (currentChar < lines[currentLine].length) {
            const t = setTimeout(() => setCurrentChar(c => c + 1), charDelay)
            return () => clearTimeout(t)
        } else {
            const t = setTimeout(() => {
                setRevealed(r => [...r, lines[currentLine]])
                setCurrentLine(l => l + 1)
                setCurrentChar(0)
            }, lineDelay)
            return () => clearTimeout(t)
        }
    }, [currentLine, currentChar, lines, charDelay, lineDelay])

    const partial = currentLine < lines.length ? lines[currentLine].slice(0, currentChar) : ''
    return { revealed, partial, done }
}

/* ─── ASCII bar ────────────────────────────────────────────── */
function AsciiBar({ value, max = 10, label }: { value: number; max?: number; label: string }) {
    const filled = Math.round(value)
    const empty = max - filled
    return (
        <div className="flex items-center gap-3 font-mono text-xs">
            <span className="w-24 text-[#888] truncate">{label}</span>
            <span className="text-primary">{'█'.repeat(filled)}</span>
            <span className="text-[#222]">{'█'.repeat(empty)}</span>
            <span className="text-[#555]">{Math.round((value / max) * 100)}%</span>
        </div>
    )
}

/* ─── Fade-in section wrapper ──────────────────────────────── */
function FadeSection({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
            className={className}
        >
            {children}
        </motion.div>
    )
}

/* ─── Terminal section heading ─────────────────────────────── */
function Cmd({ command }: { command: string }) {
    return (
        <p className="font-mono text-sm mb-6">
            <span className="text-primary font-bold">$</span>{' '}
            <span className="text-foreground">{command}</span>
        </p>
    )
}

/* ─── Build status items ───────────────────────────────────── */
const buildStatuses = [
    { name: 'MovieRoom',   status: 'Online',     dot: 'online' },
    { name: 'EduLearn',    status: 'Online',     dot: 'online' },
    { name: 'IYCGE',       status: 'Production', dot: 'online' },
    { name: 'Stepworth',   status: 'Online',     dot: 'online' },
    { name: 'Portfolio',   status: 'Production', dot: 'online' },
]

const interests = [
    'Distributed Systems',
    'Workflow Engines',
    'Automation',
    'Computer Vision',
    'APIs & Backend',
    'Computer Science',
    'Robotics & STEM',
]

const skills = [
    { label: 'Python',     value: 9 },
    { label: 'TypeScript', value: 8 },
    { label: 'React',      value: 8 },
    { label: 'Next.js',    value: 7 },
    { label: 'PHP',        value: 5 },
    { label: 'MySQL',      value: 6 },
]

const whoamiLines = [
    'Backend Developer',
    'Computer Science Student',
    'Open Source Creator',
    'Automation Builder',
    'STEM Educator',
]

/* ════════════════════════════════════════════════════════════ */
export default function HomePage() {
    const { revealed, partial, done } = useTypingSequence(whoamiLines, 50, 600)


    return (
        <div className="min-h-screen font-mono">
            <SEO
                title="Rapheal Kwabena Asomani Gadoh | Full Stack Engineer & STEM Innovator"
                description="Full Stack Engineer, Tech Innovator, and STEM Educator. Building scalable web applications, Python libraries, and automation frameworks."
                canonical="https://raphealgadoh.me/"
            />
            <StructuredData type="Person" />
            <StructuredData type="WebSite" />

            {/* ═══════════════════════════════════════════════
                HERO — Terminal login + whoami prompt
            ═══════════════════════════════════════════════ */}
            <section className="relative min-h-[90vh] flex items-center border-b border-border">
                <div className="max-w-6xl mx-auto px-6 w-full py-16">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">

                        {/* ── LEFT: Terminal session ── */}
                        <div>

                            {/* Prompt line */}
                            <motion.p
                                className="text-sm mb-2"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.3 }}
                            >
                                <span className="text-primary font-bold">rapheal@portfolio</span>
                                <span className="text-[#444]">:~$</span>
                                <span className="text-foreground ml-2">whoami</span>
                            </motion.p>

                            {/* Revealed lines */}
                            <div className="ml-0 mb-2 space-y-0.5 min-h-[8rem]">
                                {revealed.map((line, i) => (
                                    <motion.p
                                        key={i}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="text-foreground text-sm leading-relaxed"
                                    >
                                        {line}
                                    </motion.p>
                                ))}
                                {/* Currently typing line */}
                                {!done && (
                                    <p className="text-foreground text-sm leading-relaxed">
                                        {partial}
                                        <span className="animate-blink inline-block w-2 h-4 bg-foreground align-middle ml-0.5" />
                                    </p>
                                )}
                                {done && (
                                    <p className="text-[#888] text-sm mt-3">
                                        Status:{' '}
                                        <Glitch text="Building." trigger="ambient" interval={[9000, 16000]}>
                                            <span className="text-primary">Building.</span>
                                        </Glitch>
                                        <span className="animate-blink-slow inline-block w-2 h-4 bg-primary align-middle ml-1" />
                                    </p>
                                )}
                            </div>

                            {/* Next prompt */}
                            {done && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 0.4 }}
                                    className="mt-8"
                                >
                                    {/* Location */}
                                    <p className="text-xs text-[#555] mb-6 flex items-center gap-2">
                                        <span className="text-primary">$</span> echo $LOCATION
                                        <span className="text-foreground ml-2">United States 🇺🇸</span>
                                    </p>

                                    {/* CTA buttons */}
                                    <div className="flex flex-wrap gap-3">
                                        <Link to="/projects" className="btn-term-accent text-xs">
                                            <Glitch text="[ view projects ]" trigger="hover">[ view projects ]</Glitch>
                                        </Link>
                                        <Link to="/contact" className="btn-term text-xs">
                                            <Glitch text="[ get in touch ]" trigger="hover">[ get in touch ]</Glitch>
                                        </Link>
                                        <a href="/Rapheal-Kwabena-Resume.pdf" download className="btn-term text-xs">
                                            <Glitch text="[ resume.pdf ]" trigger="hover">[ resume.pdf ]</Glitch>
                                        </a>
                                    </div>

                                    {/* Social row */}
                                    <div className="flex items-center gap-4 mt-6 text-xs text-[#555]">
                                        <a href={DATA.contact.social.GitHub.url} target="_blank" rel="noopener noreferrer"
                                            className="hover:text-primary transition-colors flex items-center gap-1.5">
                                            <Github className="w-3.5 h-3.5" /> github
                                        </a>
                                        <a href={DATA.contact.social.LinkedIn.url} target="_blank" rel="noopener noreferrer"
                                            className="hover:text-primary transition-colors flex items-center gap-1.5">
                                            <Linkedin className="w-3.5 h-3.5" /> linkedin
                                        </a>
                                        <a href={DATA.contact.social.Email.url}
                                            className="hover:text-primary transition-colors flex items-center gap-1.5">
                                            <Mail className="w-3.5 h-3.5" /> email
                                        </a>
                                    </div>
                                </motion.div>
                            )}
                        </div>

                        {/* ── RIGHT: Build status panel ── */}
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 1.2, duration: 0.5 }}
                            className="hidden lg:flex flex-col gap-4"
                        >
                            {/* Build Status Panel */}
                            <div className="terminal-window">
                                <div className="terminal-titlebar">
                                    <span className="terminal-dot bg-[#ff5f57]" />
                                    <span className="terminal-dot bg-[#ffbd2e]" />
                                    <span className="terminal-dot bg-[#28c840]" />
                                    <span className="ml-3 text-[10px] text-[#444]">build-status — bash</span>
                                </div>
                                <div className="p-5 space-y-1 text-xs">
                                    <p className="text-[#555] mb-3">$ systemctl status projects</p>
                                    {buildStatuses.map(({ name, status, dot }) => (
                                        <div key={name} className="flex items-center justify-between py-1 border-b border-[#111] last:border-0">
                                            <span className="text-[#888]">{name}</span>
                                            <span className="flex items-center gap-2">
                                                <span className={`status-dot-${dot}`} />
                                                <span className="text-primary">{status}</span>
                                            </span>
                                        </div>
                                    ))}
                                    <div className="pt-4 text-[#444] space-y-1">
                                        <p>Process: 5 running, 0 failed</p>
                                        <p>Memory: nominal</p>
                                        <p className="flex items-center gap-1.5">
                                            <span className="status-dot-online" />
                                            <span className="text-primary">All systems operational</span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                    </div>
                </div>
            </section>

            {/* ═══════════════════════════════════════════════
                ABOUT — $ cat about.md
            ═══════════════════════════════════════════════ */}
            <section className="py-20 border-b border-border">
                <div className="max-w-6xl mx-auto px-6">
                    <FadeSection>
                        <Cmd command="cat about.md" />
                        <div className="terminal-window max-w-2xl">
                            <div className="terminal-titlebar">
                                <span className="terminal-dot bg-[#ff5f57]" />
                                <span className="terminal-dot bg-[#ffbd2e]" />
                                <span className="terminal-dot bg-[#28c840]" />
                                <span className="ml-3 text-[10px] text-[#444]">about.md</span>
                            </div>
                            <div className="p-6 space-y-4 text-sm">
                                <p className="text-foreground leading-relaxed">
                                    Software Engineer &amp; CS Student based in the United States.
                                </p>
                                <p className="text-[#888] leading-relaxed">
                                    {DATA.summary}
                                </p>
                                <div>
                                    <p className="text-[#555] mb-2 text-xs">Interested in:</p>
                                    <ul className="space-y-1">
                                        {interests.map(item => (
                                            <li key={item} className="text-xs text-[#888] flex items-center gap-2">
                                                <span className="text-primary">•</span> {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </FadeSection>
                </div>
            </section>

            {/* ═══════════════════════════════════════════════
                PROJECTS — $ ls projects
            ═══════════════════════════════════════════════ */}
            <section className="py-20 border-b border-border">
                <div className="max-w-6xl mx-auto px-6">
                    <FadeSection>
                        <div className="flex items-center justify-between mb-6">
                            <Cmd command="ls projects" />
                            <Link to="/projects" className="btn-term text-xs">
                                [ view all → ]
                            </Link>
                        </div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {DATA.projects.slice(0, 3).map((project) => (
                                <ProjectCard
                                    key={project.title}
                                    href={project.href}
                                    title={project.title}
                                    description={project.description}
                                    dates={project.dates}
                                    iconLists={project.iconLists}
                                    image={project.image}
                                    links={project.links}
                                />
                            ))}
                        </div>
                    </FadeSection>
                </div>
            </section>

            {/* ═══════════════════════════════════════════════
                SKILLS — $ skills
            ═══════════════════════════════════════════════ */}
            <section className="py-20 border-b border-border">
                <div className="max-w-6xl mx-auto px-6">
                    <FadeSection>
                        <Cmd command="skills --verbose" />
                        <div className="grid md:grid-cols-2 gap-12">
                            {/* ASCII progress bars */}
                            <div>
                                <p className="text-xs text-[#555] mb-4">// proficiency bars</p>
                                <div className="space-y-3">
                                    {skills.map(s => (
                                        <AsciiBar key={s.label} label={s.label} value={s.value} />
                                    ))}
                                </div>
                            </div>

                            {/* Plain list */}
                            <div>
                                <p className="text-xs text-[#555] mb-4">// full toolchain</p>
                                <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                                    {DATA.Skills.map(skill => (
                                        <span key={skill.id} className="text-xs text-[#888] flex items-center gap-2">
                                            <span className="text-primary text-[10px]">▸</span>
                                            {skill.title}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </FadeSection>
                </div>
            </section>

            {/* ═══════════════════════════════════════════════
                EXPERIENCE — $ history
            ═══════════════════════════════════════════════ */}
            <section className="py-20 border-b border-border">
                <div className="max-w-6xl mx-auto px-6">
                    <FadeSection>
                        <Cmd command="history --recent" />
                        <div className="space-y-0 max-w-2xl">
                            {DATA.work.slice(0, 4).map((role, idx) => (
                                <motion.div
                                    key={role.id}
                                    initial={{ opacity: 0, x: -12 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: idx * 0.07, duration: 0.4 }}
                                    className="flex gap-4 group py-4 border-b border-[#111] last:border-0"
                                >
                                    <div className="flex-shrink-0 w-16 text-[10px] text-[#444] pt-0.5 font-mono">
                                        {role.start.split(' ')[1]}
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-xs text-[#555] mb-0.5">
                                            <span className="text-primary">&gt;</span> {role.company}
                                        </p>
                                        <p className="text-sm text-foreground font-semibold">{role.title}</p>
                                        <p className="text-xs text-[#666] mt-1 leading-relaxed line-clamp-2">{role.description}</p>
                                    </div>
                                    <div className="flex-shrink-0 text-[10px] text-[#333] text-right pt-0.5">
                                        {(role.end as string) === 'Present' ? (
                                            <span className="text-primary">now</span>
                                        ) : role.end.split(' ')[1]}
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                        <div className="mt-6">
                            <Link to="/about" className="btn-term text-xs">
                                [ full history → ]
                            </Link>
                        </div>
                    </FadeSection>
                </div>
            </section>

            {/* ═══════════════════════════════════════════════
                CONTACT CTA — $ contact --quick
            ═══════════════════════════════════════════════ */}
            <section className="py-20">
                <div className="max-w-6xl mx-auto px-6">
                    <FadeSection>
                        <Cmd command="contact --quick" />
                        <div className="terminal-window max-w-xl">
                            <div className="terminal-titlebar">
                                <span className="terminal-dot bg-[#ff5f57]" />
                                <span className="terminal-dot bg-[#ffbd2e]" />
                                <span className="terminal-dot bg-[#28c840]" />
                                <span className="ml-3 text-[10px] text-[#444]">contact.sh</span>
                            </div>
                            <div className="p-6 space-y-3 text-sm">
                                <p className="text-[#555] text-xs">// reach out — I respond within 24h</p>
                                <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-2">
                                    <span className="text-[#555] text-xs">Email:</span>
                                    <a href="mailto:raphealasomanikwabena@gmail.com"
                                        className="text-xs text-foreground hover:text-primary transition-colors break-all">
                                        raphealasomanikwabena@<br />gmail.com
                                    </a>
                                    <span className="text-[#555] text-xs">LinkedIn:</span>
                                    <a href={DATA.contact.social.LinkedIn.url} target="_blank" rel="noopener noreferrer"
                                        className="text-xs text-foreground hover:text-primary transition-colors">
                                        /in/younggem
                                    </a>
                                    <span className="text-[#555] text-xs">GitHub:</span>
                                    <a href={DATA.contact.social.GitHub.url} target="_blank" rel="noopener noreferrer"
                                        className="text-xs text-foreground hover:text-primary transition-colors">
                                        /Rapheal-Kwabena
                                    </a>
                                </div>
                                <div className="flex gap-3 pt-3 border-t border-[#111]">
                                    <Link to="/contact" className="btn-term-accent text-xs">
                                        [ open contact form ]
                                    </Link>
                                    <a href="mailto:raphealasomanikwabena@gmail.com" className="btn-term text-xs">
                                        [ send email ]
                                    </a>
                                </div>
                            </div>
                        </div>
                    </FadeSection>
                </div>
            </section>

        </div>
    )
}
